import React from "react";
import { Badge } from "@/components/ui/badge";
import { User as UserIcon, Users } from "lucide-react";
import { useAdminUsers } from "@/lib/hooks/use-api";
import type { User } from "@/lib/types/user";
import { UserDisplayPopover } from "@/components/shared/UserDisplayPopover";

/** Legacy single-assignee relation as MultiUserDisplay consumes it. */
export interface LegacyAssignedUserRef {
  id?: string | null;
}

/**
 * Narrow an untyped `assigned_user` relation (it is a join, absent from the
 * codegen row types) down to the only thing that is read from it: its id.
 */
export function readAssignedUserRef(value: unknown): LegacyAssignedUserRef | undefined {
  if (!value || typeof value !== "object") return undefined;
  const id = (value as { id?: unknown }).id;
  return typeof id === "string" ? { id } : undefined;
}

interface MultiUserDisplayProps {
  assignedUsers?: string[];
  /** Legacy single-assignee relation; only its `id` is read. */
  assignedUser?: LegacyAssignedUserRef | null;
  maxDisplay?: number;
  showTooltip?: boolean;
  compact?: boolean;
}

export const MultiUserDisplay: React.FC<MultiUserDisplayProps> = ({
  assignedUsers = [],
  assignedUser,
  maxDisplay = 3,
  showTooltip = true,
  compact = false,
}) => {
  const { data: allUsers } = useAdminUsers();

  // Combine assigned_users array with legacy assigned_user
  const allAssignedUserIds = React.useMemo(() => {
    const userIds = new Set<string>();

    // Add users from assigned_users array (may be string IDs or objects with id)
    assignedUsers.forEach((item) => {
      const id =
        typeof item === "string"
          ? item
          : item && typeof item === "object" && "id" in item
            ? (item as { id: string }).id
            : null;
      if (id && typeof id === "string" && id.trim()) {
        userIds.add(id);
      }
    });

    // Add legacy assigned_user if not already in the set
    if (assignedUser?.id && !userIds.has(assignedUser.id)) {
      userIds.add(assignedUser.id);
    }

    return Array.from(userIds);
  }, [assignedUsers, assignedUser]);

  // Get user details for display
  const assignedUserDetails = React.useMemo(() => {
    if (!allUsers || allAssignedUserIds.length === 0) return [];

    return allAssignedUserIds
      .map((userId) => {
        const user = allUsers.find((u: User) => u.id === userId);
        if (!user) return null;

        const fullName = `${user.first_name || ""} ${user.last_name || ""}`.trim();
        const displayName = fullName || user.email || "Unknown";
        return {
          id: user.id,
          name: displayName,
          email: user.email ?? "",
          initials: (user.first_name?.[0] || "") + (user.last_name?.[0] || ""),
        };
      })
      .filter((user): user is NonNullable<typeof user> => user !== null);
  }, [allUsers, allAssignedUserIds]);

  if (assignedUserDetails.length === 0) {
    return (
      <div className="flex items-center gap-1 text-gray-400">
        <UserIcon className="h-4 w-4" />
        <span className="text-sm">Unassigned</span>
      </div>
    );
  }

  if (assignedUserDetails.length === 1) {
    const user = assignedUserDetails[0];
    const fullUser = allUsers?.find((u: User) => u.id === user.id);

    if (!fullUser) {
      return (
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1">
            <div className="w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center">
              <span className="text-xs font-medium text-blue-800 dark:text-blue-200">
                {user.initials || user.name[0]}
              </span>
            </div>
            <span className="text-sm font-medium text-card-foreground truncate">{user.name}</span>
          </div>
        </div>
      );
    }

    return (
      <div className="flex items-center gap-2">
        <UserDisplayPopover user={fullUser}>
          <div className="flex items-center gap-1 cursor-pointer hover:opacity-80 transition-opacity">
            <div className="w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center">
              <span className="text-xs font-medium text-blue-800 dark:text-blue-200">
                {user.initials || user.name[0]}
              </span>
            </div>
            <span className="text-sm font-medium text-card-foreground truncate">{user.name}</span>
          </div>
        </UserDisplayPopover>
      </div>
    );
  }

  // Multiple users
  const visibleUsers = assignedUserDetails.slice(0, maxDisplay);
  const remainingCount = assignedUserDetails.length - maxDisplay;

  return (
    <div className="flex items-center gap-1">
      {/* User avatars */}
      <div className="flex items-center -space-x-1">
        {visibleUsers.map((user, index) => {
          const fullUser = allUsers?.find((u: User) => u.id === user.id);

          if (!fullUser) {
            return (
              <div
                key={user.id}
                className={`w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center border-2 border-white ${
                  index > 0 ? "ml-0" : ""
                }`}
                title={showTooltip ? `${user.name} (${user.email})` : undefined}
              >
                <span className="text-xs font-medium text-blue-800 dark:text-blue-200">
                  {user.initials || user.name[0]}
                </span>
              </div>
            );
          }

          return (
            <UserDisplayPopover key={user.id} user={fullUser}>
              <div
                className={`w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center border-2 border-white cursor-pointer hover:opacity-80 transition-opacity ${
                  index > 0 ? "ml-0" : ""
                }`}
                title={showTooltip ? `${user.name} (${user.email})` : undefined}
              >
                <span className="text-xs font-medium text-blue-800 dark:text-blue-200">
                  {user.initials || user.name[0]}
                </span>
              </div>
            </UserDisplayPopover>
          );
        })}

        {remainingCount > 0 && (
          <div
            className="w-6 h-6 rounded-full bg-card-100 flex items-center justify-center border-2 border-white text-xs font-medium text-gray-600 dark:text-gray-400"
            title={showTooltip ? `+${remainingCount} more users` : undefined}
          >
            +{remainingCount}
          </div>
        )}
      </div>

      {/* User count badge */}
      {!compact && (
        <Badge variant="outline" className="text-xs ml-1">
          <Users className="h-3 w-3 mr-1" />
          {assignedUserDetails.length}
        </Badge>
      )}
    </div>
  );
};

// Compact version for table cells
export const CompactMultiUserDisplay: React.FC<MultiUserDisplayProps> = (props) => {
  return <MultiUserDisplay {...props} compact={true} maxDisplay={2} />;
};

// Expanded version for detailed views — extracted to own file
export { ExpandedMultiUserDisplay } from "./ExpandedMultiUserDisplay";
